import { useState, useMemo } from 'react'
import { Search, Eye, Users, RefreshCw, Wine, ShoppingBag, CheckCircle, XCircle, ShieldOff, Pencil, Save, X } from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Modal, Pagination, EmptyState, Spinner } from '../components/ui'
import { customerService, userService } from '../services/api'
import toast from 'react-hot-toast'

function CustomerTypeBadge({ listLiquor }) {
  if (listLiquor === false || listLiquor === 0) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-blue-500/15 text-blue-300 border border-blue-500/20">
        <ShoppingBag size={10}/> Non-Alcoholic
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-primary/15 text-primary border border-primary/20">
      <Wine size={10}/> Full Range
    </span>
  )
}

function CanOrderBadge({ canOrder }) {
  if (canOrder === false || canOrder === 0) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-red-500/15 text-red-400 border border-red-500/20">
        <XCircle size={10}/> On Hold
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-green-500/15 text-green-400 border border-green-500/20">
      <CheckCircle size={10}/> Can Order
    </span>
  )
}

export default function Customers() {
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [selectedCustomer, setSelectedCustomer] = useState(null)
  const [typeFilter, setTypeFilter] = useState('all')
  const [tierFilter, setTierFilter] = useState('all')
  const [perPage, setPerPage] = useState(20)
  const [selectedId, setSelectedId] = useState(null)
  const [editContact, setEditContact] = useState(false)
  const [contactForm, setContactForm] = useState({ email: '', phone: '' })
  const qc = useQueryClient()

  const { data: cd, isLoading, refetch } = useQuery({
    queryKey: ['admin', 'customers'],
    queryFn: () => customerService.getAll({ limit: 500 }),
    staleTime: 2 * 60 * 1000,
  })

  const toggleCanOrder = useMutation({
    mutationFn: ({ id, can_order }) => customerService.update(id, { can_order }),
    onSuccess: (_, vars) => {
      qc.setQueryData(['admin', 'customers'], old => {
        const list = Array.isArray(old) ? old : (old?.items ?? old?.customers ?? [])
        const updated = list.map(c => c.id === vars.id ? { ...c, can_order: vars.can_order } : c)
        return Array.isArray(old) ? updated : { ...old, items: updated }
      })
      setSelectedCustomer(prev => prev?.id === vars.id ? { ...prev, can_order: vars.can_order } : prev)
      toast.success(vars.can_order ? 'Account unlocked — customer can now order' : 'Account put on hold')
    },
    onError: (err) => toast.error(err.message || 'Failed to update'),
  })

  const updateContact = useMutation({
    mutationFn: ({ id, email, phone }) => customerService.update(id, { email, phone }),
    onSuccess: (_, vars) => {
      qc.setQueryData(['admin', 'customers'], old => {
        const list = Array.isArray(old) ? old : (old?.items ?? old?.customers ?? [])
        const updated = list.map(c => c.id === vars.id ? { ...c, email: vars.email, phone: vars.phone } : c)
        return Array.isArray(old) ? updated : { ...old, items: updated }
      })
      setSelectedCustomer(prev => prev?.id === vars.id ? { ...prev, email: vars.email, phone: vars.phone } : prev)
      setEditContact(false)
      toast.success('Contact details updated')
    },
    onError: (err) => toast.error(err.message || 'Failed to save contact details'),
  })

  // Fetch users list to match customers → user_id → 2FA status
  const { data: usersData } = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () => userService.getAll(),
    staleTime: 2 * 60 * 1000,
  })
  const usersById = useMemo(() => {
    const map = {}
    if (Array.isArray(usersData)) usersData.forEach(u => { map[u.id] = u })
    return map
  }, [usersData])

  const reset2fa = useMutation({
    mutationFn: (userId) => userService.reset2fa(userId),
    onSuccess: (res) => {
      toast.success(res?.message || '2FA cleared — customer can now log in with password only.')
      qc.invalidateQueries(['admin', 'users'])
    },
    onError: (err) => toast.error(err.message || 'Failed to reset 2FA'),
  })

  const raw = Array.isArray(cd) ? cd : (cd?.items ?? cd?.customers ?? [])

  const filtered = useMemo(() => {
    let list = raw
    if (typeFilter === 'full')    list = list.filter(c => c.list_liquor !== false && c.list_liquor !== 0)
    if (typeFilter === 'nonalc')  list = list.filter(c => c.list_liquor === false || c.list_liquor === 0)
    if (typeFilter === 'onhold')  list = list.filter(c => c.can_order === false || c.can_order === 0)
    if (tierFilter !== 'all')     list = list.filter(c => String(c.price_list_tier || 2) === tierFilter)
    if (search) {
      const q = search.toLowerCase()
      list = list.filter(c =>
        (c.name||'').toLowerCase().includes(q) ||
        (c.email||'').toLowerCase().includes(q) ||
        (c.code||'').toLowerCase().includes(q) ||
        String(c.id).includes(q)
      )
    }
    return list
  }, [raw, search, typeFilter, tierFilter])

  const availableTiers = useMemo(() => [...new Set(raw.map(c => c.price_list_tier || 2))].sort((a, b) => a - b), [raw])

  useMemo(() => setPage(1), [search, typeFilter, tierFilter, perPage])
  const paginated = filtered.slice((page-1)*perPage, page*perPage)

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Customers</h1>
          <p className="text-gray-400 text-sm mt-0.5">{filtered.length} of {raw.length} customers</p>
        </div>
        <button onClick={() => refetch()} className="btn-secondary flex items-center gap-2 text-sm">
          <RefreshCw size={14}/> Refresh
        </button>
      </div>

      {/* Search + filter bar */}
      <div className="bg-surface-card border border-surface-border rounded-xl p-4 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"/>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by name, code, email or ID..."
            className="input-field w-full pl-9 text-sm"
          />
        </div>
        <div className="flex gap-1 bg-black/30 border border-surface-border rounded-lg p-1 shrink-0 flex-wrap">
          {[['all','All'],['full','Full Range'],['nonalc','Non-Alcoholic'],['onhold','On Hold']].map(([val, label]) => (
            <button
              key={val}
              onClick={() => setTypeFilter(val)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                typeFilter === val ? 'bg-primary text-white' : 'text-gray-400 hover:text-white'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <select
          value={tierFilter}
          onChange={e => setTierFilter(e.target.value)}
          className="input-field text-sm w-36 shrink-0"
        >
          <option value="all">All Tiers</option>
          {availableTiers.map(t => <option key={t} value={String(t)}>Tier {t}</option>)}
        </select>
      </div>

      <Pagination page={page} total={filtered.length} perPage={perPage} onPerPageChange={setPerPage} onChange={setPage} position="top"/>

      <div className="bg-surface-card border border-surface-border rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16"><Spinner size="lg"/></div>
        ) : filtered.length === 0 ? (
          <EmptyState icon={Users} title="No customers found" message="No customers match your search"/>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="admin-thead border-b border-surface-border">
                  {['ID','Code','Name','Email','Customer Type','Can Order','Price Tier','Actions'].map(h=>(
                    <th key={h} className="text-left px-4 py-3 text-gray-400 font-medium text-xs uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginated.map(c => (
                  <tr key={c.id} onClick={() => { setSelectedId(c.id); setEditContact(false); setSelectedCustomer(c) }}
                    className={`border-b border-surface-border/50 transition-colors cursor-pointer ${selectedId === c.id ? 'bg-primary/10' : 'hover:bg-white/[0.03]'}`}>
                    <td className="px-4 py-3 text-gray-500 text-xs">{c.id}</td>
                    <td className="px-4 py-3 text-gray-300 text-xs font-mono">{c.code || '—'}</td>
                    <td className="px-4 py-3 text-white font-medium">{c.name || '—'}</td>
                    <td className="px-4 py-3 text-gray-400 text-xs">{c.email || '—'}</td>
                    <td className="px-4 py-3"><CustomerTypeBadge listLiquor={c.list_liquor}/></td>
                    <td className="px-4 py-3">
                      <CanOrderBadge canOrder={c.can_order}/>
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-gold/10 text-gold border border-gold/20">
                        Tier {c.price_list_tier || 2}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={e => { e.stopPropagation(); setEditContact(false); setSelectedCustomer(c) }}
                        className="admin-btn-solid inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium hover:opacity-90 transition-opacity"
                        title="View / edit customer"
                      >
                        <Eye size={13}/> View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Pagination page={page} total={filtered.length} perPage={perPage} onPerPageChange={setPerPage} onChange={setPage} position="bottom"/>

      {selectedCustomer && (
        <Modal title={`Customer: ${selectedCustomer.name || `#${selectedCustomer.id}`}`} onClose={() => { setSelectedCustomer(null); setEditContact(false) }}>
          <div className="space-y-0 text-sm">
            {[
              ['ID',         selectedCustomer.id],
              ['Code',       selectedCustomer.code],
              ['Name',       selectedCustomer.name],
              ['Price Tier', `Tier ${selectedCustomer.price_list_tier || 2}`],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between py-2.5 border-b border-surface-border/50">
                <span className="text-gray-500">{k}</span>
                <span className="text-white">{v || '—'}</span>
              </div>
            ))}

            {/* Editable contact details — read-only until Edit is clicked */}
            <div className="py-2.5 border-b border-surface-border/50">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-500">Contact details</span>
                {!editContact ? (
                  <button
                    onClick={() => { setContactForm({ email: selectedCustomer.email || '', phone: selectedCustomer.phone || '' }); setEditContact(true) }}
                    className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-lg border border-surface-border text-gray-300 hover:text-white hover:border-primary/50 transition-colors"
                  >
                    <Pencil size={11}/> Edit
                  </button>
                ) : (
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => updateContact.mutate({ id: selectedCustomer.id, email: contactForm.email.trim(), phone: contactForm.phone.trim() })}
                      disabled={updateContact.isPending}
                      className="admin-btn-solid inline-flex items-center gap-1 text-xs px-2 py-1 rounded-lg hover:opacity-90 disabled:opacity-60"
                    >
                      <Save size={11}/> {updateContact.isPending ? 'Saving…' : 'Save'}
                    </button>
                    <button
                      onClick={() => setEditContact(false)}
                      className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-lg border border-surface-border text-gray-400 hover:text-white transition-colors"
                    >
                      <X size={11}/> Cancel
                    </button>
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <label className="flex items-center gap-2">
                  <span className="text-gray-500 w-12 shrink-0 text-xs">Email</span>
                  <input
                    type="email" readOnly={!editContact}
                    value={editContact ? contactForm.email : (selectedCustomer.email || '')}
                    onChange={e => setContactForm(f => ({ ...f, email: e.target.value }))}
                    placeholder="—"
                    className={`input-field flex-1 text-sm ${editContact ? '' : 'opacity-70 cursor-default'}`}
                  />
                </label>
                <label className="flex items-center gap-2">
                  <span className="text-gray-500 w-12 shrink-0 text-xs">Phone</span>
                  <input
                    type="tel" readOnly={!editContact}
                    value={editContact ? contactForm.phone : (selectedCustomer.phone || '')}
                    onChange={e => setContactForm(f => ({ ...f, phone: e.target.value }))}
                    placeholder="—"
                    className={`input-field flex-1 text-sm ${editContact ? '' : 'opacity-70 cursor-default'}`}
                  />
                </label>
              </div>
            </div>
            <div className="flex justify-between py-2.5 border-b border-surface-border/50">
              <span className="text-gray-500">Customer Type</span>
              <CustomerTypeBadge listLiquor={selectedCustomer.list_liquor}/>
            </div>
            <div className="flex justify-between items-center py-2.5 border-b border-surface-border/50">
              <span className="text-gray-500">Can Order</span>
              <div className="flex items-center gap-2">
                <CanOrderBadge canOrder={selectedCustomer.can_order}/>
                <button
                  onClick={() => toggleCanOrder.mutate({ id: selectedCustomer.id, can_order: !(selectedCustomer.can_order !== false) })}
                  className={`text-xs px-2 py-1 rounded-lg border transition-colors ${
                    selectedCustomer.can_order !== false
                      ? 'border-red-500/30 text-red-400 hover:bg-red-500/10'
                      : 'border-green-500/30 text-green-400 hover:bg-green-500/10'
                  }`}
                >
                  {selectedCustomer.can_order !== false ? 'Put on Hold' : 'Unlock'}
                </button>
              </div>
            </div>
            <div className="flex justify-between py-2.5">
              <span className="text-gray-500">Account Status</span>
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                selectedCustomer.active !== false
                  ? 'bg-green-500/15 text-green-400 border border-green-500/20'
                  : 'bg-red-500/15 text-red-400 border border-red-500/20'
              }`}>
                {selectedCustomer.active !== false ? 'Active' : 'Inactive'}
              </span>
            </div>

            {/* 2FA status + admin reset */}
            {(() => {
              const linkedUser = selectedCustomer.user_id ? usersById[selectedCustomer.user_id] : null
              const hasTotp      = linkedUser?.totp_enabled
              const hasEmailOtp  = linkedUser?.email_2fa_enabled
              const has2fa       = hasTotp || hasEmailOtp
              const method       = hasTotp ? 'Authenticator App' : hasEmailOtp ? 'Email OTP' : null
              return (
                <div className="flex justify-between items-center py-2.5 border-t border-surface-border/50 mt-1">
                  <span className="text-gray-500">Two-Factor Auth</span>
                  <div className="flex items-center gap-2">
                    {linkedUser ? (
                      <>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                          has2fa
                            ? 'bg-primary/15 text-primary border border-primary/20'
                            : 'bg-white/5 text-gray-500 border border-white/10'
                        }`}>
                          {has2fa ? method : 'Disabled'}
                        </span>
                        {has2fa && (
                          <button
                            onClick={() => {
                              if (window.confirm(`Clear all 2FA for ${selectedCustomer.name}? They will log in with password only until they re-enable it.`)) {
                                reset2fa.mutate(linkedUser.id)
                              }
                            }}
                            disabled={reset2fa.isPending}
                            className="flex items-center gap-1 text-xs px-2 py-1 rounded-lg border border-orange-500/30 text-orange-400 hover:bg-orange-500/10 transition-colors"
                            title="Clear all 2FA — use when customer is locked out (lost phone / new device)"
                          >
                            <ShieldOff size={11}/>
                            {reset2fa.isPending ? 'Resetting…' : 'Reset 2FA'}
                          </button>
                        )}
                      </>
                    ) : (
                      <span className="text-xs text-gray-600">No linked login account</span>
                    )}
                  </div>
                </div>
              )
            })()}
          </div>
        </Modal>
      )}
    </div>
  )
}
